import { useCallback, useRef, useState } from "react";
import type { AppDispatch } from "../../../../redux/store";
import {
  clearUnread,
  setLastReadAt,
  updateConversationList,
} from "../../../../redux/slices/chatSlice";
import conversationCacheDB from "../../../../services/conversationCacheDB";

export interface UseUnreadDividerReturn {
  firstUnreadMsgId: string | null;
  setFirstUnreadMsgId: React.Dispatch<React.SetStateAction<string | null>>;
  dividerUnreadCount: number;
  setDividerUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  unreadBadgeCount: number;
  setUnreadBadgeCount: React.Dispatch<React.SetStateAction<number>>;
  unreadBadgeCountRef: React.MutableRefObject<number>;
  dividerClearTimerRef: React.MutableRefObject<NodeJS.Timeout | null>;
  lastReadAtRef: React.MutableRefObject<string | null>;
  persistRead: (
    chatId: string,
    timestamp: string,
    remainingUnreadCount?: number,
  ) => void;
  persistReadRef: React.MutableRefObject<
    (chatId: string, timestamp: string, remainingUnreadCount?: number) => void
  >;
  clearDivider: (immediate?: boolean) => void;
}

export function useUnreadDivider(dispatch: AppDispatch): UseUnreadDividerReturn {
  const [firstUnreadMsgId, setFirstUnreadMsgId] = useState<string | null>(null);
  const [dividerUnreadCount, setDividerUnreadCount] = useState(0);
  const [unreadBadgeCount, setUnreadBadgeCount] = useState(0);

  const unreadBadgeCountRef = useRef(0);
  const dividerClearTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastReadAtRef = useRef<string | null>(null);

  const persistRead = useCallback(
    (chatId: string, timestamp: string, remainingUnreadCount = 0) => {
      if (!chatId || !timestamp) return;
      lastReadAtRef.current = timestamp;
      dispatch(setLastReadAt({ chatId, timestamp }));
      if (remainingUnreadCount > 0) {
        dispatch(
          updateConversationList({ id: chatId, unreadCount: remainingUnreadCount }),
        );
      } else {
        dispatch(clearUnread(chatId));
      }
      conversationCacheDB.patch(chatId, {
        lastReadAt: timestamp,
        unreadCount: remainingUnreadCount,
        lastUpdated: new Date().toISOString(),
      });
    },
    [dispatch],
  );

  // Stable ref so effects with [] deps always call the latest persistRead.
  const persistReadRef = useRef(persistRead);
  persistReadRef.current = persistRead;

  const clearDivider = useCallback((immediate = false) => {
    if (dividerClearTimerRef.current) clearTimeout(dividerClearTimerRef.current);
    if (immediate) {
      setFirstUnreadMsgId(null);
      setDividerUnreadCount(0);
    } else {
      dividerClearTimerRef.current = setTimeout(() => {
        setFirstUnreadMsgId(null);
        setDividerUnreadCount(0);
      }, 3000);
    }
  }, []);

  return {
    firstUnreadMsgId,
    setFirstUnreadMsgId,
    dividerUnreadCount,
    setDividerUnreadCount,
    unreadBadgeCount,
    setUnreadBadgeCount,
    unreadBadgeCountRef,
    dividerClearTimerRef,
    lastReadAtRef,
    persistRead,
    persistReadRef,
    clearDivider,
  };
}
